/**
 * Erros de upload, tipados.
 *
 * A separacao importa: `FILE_TOO_LARGE`, `INVALID_FILE_TYPE` e
 * `MISSING_FILE_EXTENSION` sao falhas de validacao detectaveis *antes* de
 * qualquer requisicao — o arquivo nunca deveria sair da maquina do usuario.
 * Os demais sao falhas do fluxo de duas fases (URL, PUT no storage,
 * confirmacao no servidor) e admitem nova tentativa.
 */
export const FileUploadErrorCode = {
  FileTooLarge: "FILE_TOO_LARGE",
  InvalidFileType: "INVALID_FILE_TYPE",
  MissingFileExtension: "MISSING_FILE_EXTENSION",
  EmptyFile: "EMPTY_FILE",
  UploadUrlFailed: "UPLOAD_URL_FAILED",
  UploadUrlExpired: "UPLOAD_URL_EXPIRED",
  UploadFailed: "UPLOAD_FAILED",
  RegistrationFailed: "REGISTRATION_FAILED",
} as const;

export type FileUploadErrorCode =
  (typeof FileUploadErrorCode)[keyof typeof FileUploadErrorCode];

const FILE_UPLOAD_ERROR_MESSAGES: Record<FileUploadErrorCode, string> = {
  [FileUploadErrorCode.FileTooLarge]: "Arquivo maior que o limite permitido.",
  [FileUploadErrorCode.InvalidFileType]: "Tipo de arquivo não suportado.",
  [FileUploadErrorCode.MissingFileExtension]:
    "O arquivo precisa ter uma extensão no nome.",
  [FileUploadErrorCode.EmptyFile]: "O arquivo está vazio.",
  [FileUploadErrorCode.UploadUrlFailed]: "Falha ao gerar URL de upload.",
  [FileUploadErrorCode.UploadUrlExpired]: "URL de upload expirou.",
  [FileUploadErrorCode.UploadFailed]: "Falha ao enviar arquivo.",
  [FileUploadErrorCode.RegistrationFailed]:
    "O arquivo foi enviado, mas não pôde ser confirmado no servidor.",
};

/** Erros de validacao — nao adianta tentar de novo com o mesmo arquivo. */
const NON_RETRYABLE: ReadonlySet<FileUploadErrorCode> = new Set([
  FileUploadErrorCode.FileTooLarge,
  FileUploadErrorCode.InvalidFileType,
  FileUploadErrorCode.MissingFileExtension,
  FileUploadErrorCode.EmptyFile,
]);

export class FileUploadError extends Error {
  readonly code: FileUploadErrorCode;

  constructor(code: FileUploadErrorCode, message?: string) {
    super(message ?? FILE_UPLOAD_ERROR_MESSAGES[code]);
    this.name = "FileUploadError";
    this.code = code;
  }
}

export function getFileUploadErrorMessage(code: FileUploadErrorCode): string {
  return FILE_UPLOAD_ERROR_MESSAGES[code];
}

export function isRetryableFileUploadError(code: FileUploadErrorCode): boolean {
  return !NON_RETRYABLE.has(code);
}
